import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../features/axiosInstance/axiosInstance";
import { ROUTES } from "../routes";
import { useTranslation } from "react-i18next";

const EmailConfirmedPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const confirmEmail = async () => {
      try {
        const userId = searchParams.get("userId");
        const token = searchParams.get("token");

        if (!userId || !token) {
          setError(t("emailConfirmation.errors.missingParameters"));
          setIsLoading(false);
          return;
        }

        const response = await axiosInstance.get("/account/confirm-email", {
          params: {
            userId,
            token,
          },
        });

        if (response.status === 200) {
          const email = localStorage.getItem("pendingConfirmationEmail");
          setConfirmedEmail(email);
          localStorage.removeItem("pendingConfirmationEmail");

          const timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate(ROUTES.LOGIN);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          return;
        } else {
          setError(t("emailConfirmation.errors.confirmationFailed"));
        }
      } catch (error: any) {
        setError(
          error.response?.data?.message ||
            t("emailConfirmation.errors.generalError")
        );
      } finally {
        setIsLoading(false);
      }
      return;
    };

    confirmEmail();
  }, [navigate, searchParams, t]);

  return (
    <div className="max-w-md mx-auto" style={{ height: `calc(100vh - var(--header-height, 80px))` }}>
      <div className="flex flex-col items-center justify-center" style={{ height: `calc(100vh - var(--header-height, 80px))` }}>
        <div className="rounded-3xl shadow-[0px_5px_15px_rgba(0,0,0,0.1)] p-4 sm:p-8 w-full relative border border-primary-40 dark:border-primary-90">
          {isLoading && (
            <>
              <div className="flex justify-center mb-6">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-60 dark:border-primary-40"></div>
              </div>
              <h2 className="text-lg font-medium text-center text-grey-20 dark:text-white">
                {t("emailConfirmation.confirmingEmailAddress")}
              </h2>
            </>
          )}

          {!isLoading && error && (
            <>
              <div className="flex justify-center mb-6">
                <svg className="w-16 h-16 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-center mb-4 text-grey-20 dark:text-white">
                {t("emailConfirmation.emailConfirmationFailed")}
              </h1>
              <p className="mt-4 text-sm p-3 rounded-full text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                {error}
              </p>
            </>
          )}

          {!isLoading && !error && (
            <>
              <div className="flex justify-center mb-6">
                <svg className="w-16 h-16 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-center mb-4 text-grey-20 dark:text-white">
                {t("emailConfirmation.emailConfirmedSuccessfully")}
              </h1>
              <p className="text-center mb-6 text-grey-20 dark:text-white">
                {t("emailConfirmation.yourEmailAddress")}
                <span className="ml-2 bg-primary-95 dark:bg-primary-20 px-3 py-1 rounded-full inline-block text-center text-primary-10 dark:text-primary-90 font-medium">
                  {confirmedEmail || "your@email.com"}
                </span>{" "}
                {t("emailConfirmation.hasBeenConfirmed")}
              </p>
              <div className="flex justify-center mt-6">
                <p className="text-sm text-grey-50 dark:text-grey-70">
                  {t("emailConfirmation.redirectingToLogin", {
                    count: countdown,
                  })}
                </p>
              </div>
              <div className="flex justify-center mt-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-60 dark:border-primary-40"></div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmedPage;
